import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { User, Team, Tag, UserStatus } from '../types/user';
import TeamBadge from './TeamBadge';
import TagBadge from './TagBadge';
import TeamSelector from './TeamSelector';
import TagSelector from './TagSelector';
import RoleSelector from './RoleSelector';
import { UserRole } from '../../settings/UserRoles';
import { capitalize } from 'lodash';
import { Invitation } from '../../../serverActions/admin';
import { ArrowPathIcon, PaperAirplaneIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useAdmin } from '../../../providers/AdminProvider';

interface UserTableRowProps {
  user: User;
  teams: Team[];
  tags: Tag[];
  isSelected: boolean;
  onSelectUser: (userId: string) => void;
  getTeamById: (teamId: string) => Team | undefined;
  getTagById: (tagId: string) => Tag | undefined;
  onResendInvite: (invitations:Invitation[]) => Promise<void>;
  onRevokeInvite: (invitations:Invitation[]) => Promise<void>;
  removeUser: (userId: string[]) => Promise<void>;
  onRowChange: (userId: string, changes: Partial<User>) => void;
}

const UserTableRow: React.FC<UserTableRowProps> = ({
  user,
  teams,
  tags,
  isSelected,
  onSelectUser,
  getTeamById,
  getTagById,
  onResendInvite,
  onRevokeInvite,
  removeUser,
  onRowChange
}) => {
  const {pendingUserChanges} = useAdmin();

  const pendingTeams = useMemo(() => {
    return pendingUserChanges[user.id]?.teams || user.teams;
  }, [pendingUserChanges, user.teams, user.id]);

  const teamsWithState = useMemo(() => {
    const allTeams = Array.from(new Set([...user.teams, ...pendingTeams]));
    return allTeams.map(teamId => {
      const team = getTeamById(teamId);
      if (!team) return null;
      const isAdded = !user.teams.includes(teamId) && pendingTeams.includes(teamId);
      const isRemoved = user.teams.includes(teamId) && !pendingTeams.includes(teamId);
      return {
        team: team.id,
        state: isAdded ? 'added' : isRemoved ? 'removed' : 'none'
      };
    }).filter(Boolean) as { team: string; state: 'added' | 'removed' | 'none' }[];
  }, [pendingTeams, user.teams, getTeamById]);

  const pendingTag = useMemo(() => {
    const pendingTags = pendingUserChanges[user.id]?.tags;
    return pendingTags?.[0] ?? user.tags?.[0] ?? '';
  }, [pendingUserChanges, user.tags, user.id]);

  const pendingRole = useMemo(() => {
    return pendingUserChanges[user.id]?.role || user.role;
  }, [pendingUserChanges, user.role, user.id]);

  const handleTeamChange = (teamsToAdd: string[], teamsToRemove: string[]) => {
    const updatedTeams = [
      ...pendingTeams.filter(team => !teamsToRemove.includes(team)),
      ...teamsToAdd
    ];
    onRowChange(user.id, { teams: updatedTeams });
  };

  const handleTagChange = (tagToAdd: Tag) => {
    onRowChange(user.id, { tags: [tagToAdd.id] });
  };

  const handleRoleChange = (role: UserRole) => {
    onRowChange(user.id, { role });
  };

  const getStatusBadgeColor = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Active:
        return 'bg-blue-100 text-blue-800';
      case UserStatus.Invited:
        return 'bg-gray-100 text-gray-800';
      case UserStatus.Expired:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <tr className={isSelected ? 'bg-blue-50' : undefined}>
      <td className="whitespace-nowrap py-4 pr-3 text-xs sm:text-sm font-medium text-gray-900 pl-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            className={`h-3 sm:h-4 w-3 sm:w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3 cursor-pointer`}
            checked={isSelected}
            onChange={() => onSelectUser(user.id)}
          />
          <div className='flex flex-row items-center gap-x-4'>
            <div className='flex flex-col gap-y-1 min-w-40'>
              {user.name}
              <span className='font-normal text-xs'>{user.email}</span>
            </div>
            <div className="flex items-center">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(user.status)}`}>
                {capitalize(user.status)}
              </span>
            </div>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-xs sm:text-sm">
        <div className="flex items-center">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800 gap-x-1 ${pendingRole === user.role ? 'bg-gray-100 ' : 'bg-yellow-100'}`}>
            {capitalize(pendingRole.replace('org:', '').replace('_', ' '))}
            {pendingRole !== user.role && <ArrowPathIcon className="h-3 w-3 ml-1 text-yellow-900" aria-hidden="true" />}
          </span>
        </div>
      </td>
      <td className="px-3 py-4 text-xs sm:text-sm text-gray-500">
        <div className="flex flex-wrap gap-2">
          {teamsWithState.slice(0, 2).map((teamWithState) => {
            const team = getTeamById(teamWithState.team);
            return team && (
              <TeamBadge
                key={teamWithState.team}
                team={team}
                changed={teamWithState.state}
              />
            );
          })}
          {teamsWithState.length > 2 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title={teamsWithState.slice(2).map((teamWithState) => `${getTeamById(teamWithState.team)?.name ?? ""} (${teamWithState.state})`).join('\n')} >
              +{teamsWithState.length - 2}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-4 text-xs sm:text-sm text-gray-500 min-w-28">
        <div className="flex flex-wrap gap-2">
          {[pendingTag].map((tagId) => {
            const tag = getTagById(tagId);
            return tag && (
              <TagBadge
                key={tagId}
                tag={tag}
                changed={pendingTag !== (user.tags[0] || '')}
              />
            );
          })}
        </div>
      </td>
      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-xs sm:text-sm font-medium sm:pr-6">
        <div className="flex justify-start space-x-2 items-center">
          <div className="static" style={{ position: 'static', zIndex: 'auto' }}>
            <RoleSelector
              selectedRole={user.role}
              onSelectRole={handleRoleChange}
              disabled={user.status !== UserStatus.Active}
            />
          </div>
          <div className="static" style={{ position: 'static', zIndex: 'auto' }}>
            <TeamSelector
              teams={teams}
              selectedTeams={user.teams}
              onSelectTeam={handleTeamChange}
              disabled={user.status !== UserStatus.Active}
            />
          </div>
          <div className="static" style={{ position: 'static', zIndex: 'auto' }}>
            <TagSelector
              tags={tags}
              selectedTags={user.tags[0] ? tags.filter((t) => t.id === user.tags[0]) : []}
              onSelectTag={handleTagChange}
              disabled={user.status !== UserStatus.Active}
            />
          </div>
          {user.status === UserStatus.Active && <div>
            <button
              className="inline-flex items-center px-1 sm:px-3 py-1 sm:py-2 gap-x-1 rounded-md text-xs sm:text-sm font-medium bg-red-50 text-gray-800 hover:bg-red-100 border border-gray-300"
              onClick={() => { removeUser([user.id]).catch(() => {}) }}
            >
              <TrashIcon className="h-3 w-3 mr-1" aria-hidden="true" />
              Remove
            </button>
          </div>}
          {user.status !== UserStatus.Active && <div>
            <button
              className="inline-flex items-center px-1 sm:px-3 py-1 sm:py-2 gap-x-1 rounded-md text-xs sm:text-sm font-medium bg-gray-50 text-gray-800 hover:bg-gray-100 border border-gray-300"
              onClick={() => { onResendInvite([{ id: user.id, organization_id: user.teams[0], email_address: user.email, status: user.status, role:user.role }]).catch(() => {}) }}
            >
              <PaperAirplaneIcon className="h-3 w-3 mr-1" aria-hidden="true" />
              Resend
            </button>
          </div>}
          {user.status === UserStatus.Invited && <div>
            <button
              className="inline-flex items-center px-1 sm:px-3 py-1 sm:py-2 gap-x-1 rounded-md text-xs sm:text-sm font-medium bg-red-50 text-red-800 hover:bg-red-100 border border-gray-300"
              onClick={() => { onRevokeInvite([{ id: user.id, organization_id: user.teams[0], email_address: user.email, status: user.status, role: user.role }]).catch(() => {}) }}
            >
              <TrashIcon className="h-3 w-3 mr-1" aria-hidden="true" />
              Revoke
            </button>
          </div>}
        </div>
      </td>
    </tr>
  );
};

export default UserTableRow;
